import React, { useRef, useState, useEffect } from "react";
import KRGlue from "@lyracom/embedded-form-glue";
import {
  getPrintingPaymentStatusCallback,
  PAYMENT_GATEWAY_PUBLIC_KEY,
  postCreatePrintingPaymentForm,
  postSendPrintingOrderEmail,
} from "../../core/api/Api";
import { useNavigate, useLocation } from "react-router-dom";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../core/constants/constants";
import { useTranslation } from "react-i18next";
import { showToast } from "../../core/common-service/CommonService";
import { trackCheckoutEvent } from "../../core/utils/trackCheckoutEvent";
import { getCurrencyForLocale, getLocaleFromPath, DEFAULT_LOCALE } from "../../config/locales";

const PrintingPaymentPage = () => {
  const mounted = useRef(true);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const activeLocale = getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
  const currency = getCurrencyForLocale(activeLocale);
  const {
    printingOrderId,
    price: rawPrice,
    copies,
  } = location.state || {};
  const price = rawPrice != null ? parseFloat(rawPrice) : undefined;

  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    window.scroll(0, 0);
    async function setupPaymentForm() {
      const endpoint = PAYMENT_GATEWAY_ENDPOINT;
      const publicKey = PAYMENT_GATEWAY_PUBLIC_KEY;

      try {
        postCreatePrintingPaymentForm({ price })
          .then(async (res) => {
            const response = res?.data?.success;
            if (response) {
              const formToken = response?.formToken;
              const orderId = response?.order_id;

              const { KR } = await KRGlue.loadLibrary(
                endpoint,
                publicKey
              ); /* Load the remote library */

              await KR.setFormConfig({
                formToken,
                "kr-language":
                  i18n.language /* to update initialization parameter */,
              });

              await KR.renderElements(
                "#myPaymentForm"
              ); /* Render the payment form into myPaymentForm div */

              await KR.onSubmit(async (res) => {
                const orderStatus = res.clientAnswer.orderStatus;
                if (orderStatus === "PAID") {
                  const lang = activeLocale;
                  getPrintingPaymentStatusCallback(orderId)
                    .then((res) => {
                      const response = res?.data?.success;
                      if (response) {
                        trackCheckoutEvent({
                          eventType: "print",
                          transactionId: orderId,
                          value: price ?? response?.price ?? 0,
                          items: [
                            {
                              item_id: `printing_${orderId}`,
                              item_name: "Book Printing",
                              item_category: "Printing",
                              price: price ?? response?.price ?? 0,
                              quantity: copies || 1,
                            },
                          ],
                          output: "success",
                        });
                      }
                      const redirect = () => {
                        window.location.href = `/${lang}/thankyou/purchase?order=${orderId}`;
                      };
                      if (printingOrderId) {
                        postSendPrintingOrderEmail(printingOrderId)
                          .finally(() => redirect());
                      } else {
                        redirect();
                      }
                    })
                    .catch((e) => {
                      trackCheckoutEvent({
                        eventType: "print",
                        transactionId: orderId,
                        currency: e?.response?.data?.currency || currency.code,
                        value: price ?? 0,
                        items: [
                          {
                            item_id: `printing_${orderId}`,
                            item_name: "Book Printing",
                            item_category: "Printing",
                            price: price ?? 0,
                            quantity: copies || 1,
                          },
                        ],
                        output: "fail",
                      });
                      window.location.href = `/${lang}/thankyou/purchase?order=${orderId}`;
                    });
                }
                return false;
              });

              await KR.onError(async (res) => {
                getPrintingPaymentStatusCallback(orderId)
                  .then((resp) => {
                    trackCheckoutEvent({
                      eventType: "print",
                      transactionId: orderId,
                      value: price ?? resp?.data?.price ?? 0,
                      items: [
                        {
                          item_id: `printing_${orderId}`,
                          item_name: "Book Printing",
                          item_category: "Printing",
                          price: price ?? resp?.data?.price ?? 0,
                          quantity: copies || 1,
                        },
                      ],
                      output: "fail",
                    });
                  })
                  .catch((error) => {
                    let errorPrice = 0;
                    if (error?.response?.data?.price !== undefined) {
                      errorPrice = error.response.data.price;
                    }
                    trackCheckoutEvent({
                      eventType: "print",
                      transactionId: orderId,
                      value: price ?? errorPrice,
                      items: [
                        {
                          item_id: `printing_${orderId}`,
                          item_name: "Book Printing",
                          item_category: "Printing",
                          price: price ?? errorPrice,
                          quantity: copies || 1,
                        },
                      ],
                      output: "fail",
                    });
                  });
              });
            }
          })
          .catch(() => {
            showToast({ type: "error", message: t("payment_form_setup_failed") });
            navigate(-1);
          });
      } catch (error) {
        navigate(-1);
        setMessage(error + "");
      }
    }
    setupPaymentForm();
    return () => {
      mounted.current = false;
    };
  }, [i18n.language, navigate, price, printingOrderId, copies]);

  return (
    <section className="aai-features py-100 payment">
<div className="form">
        <div className="d-flex align-items-center justify-content-center">
          <div id="myPaymentForm">
            <div className="kr-smart-form" kr-card-form-expanded="true"></div>
          </div>
          <div data-test="payment-message">{message}</div>
        </div>
      </div>
    </section>
  );
};

export default PrintingPaymentPage;
